import api from "src/services/api";

export type ModoSalida = "entrega" | "consumo";

export interface Tema {
  id: number;
  nombre: string;
  descripcion: string | null;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  activo: boolean;
  creado_en?: string | null;
  controla_categorias?: boolean;
  modo_salida?: ModoSalida;
  etiqueta_consumo?: string;
}

export interface MiTemaItem extends Tema {
  es_admin: boolean;
  es_entregador: boolean;
  es_elegible: boolean;
}

export interface TemaPayload {
  nombre: string;
  descripcion?: string | null;
  fecha_inicio: string;
  fecha_fin: string;
  activo: boolean;
  controla_categorias: boolean;
  modo_salida: ModoSalida;
  etiqueta_consumo?: string | null;
}

export interface StockBucket {
  categoria: string | null;
  identificador: string | null;
  saldo: number;
  punto_servicio?: string | null;
  punto_servicio_nombre?: string | null;
}

export interface EntregaPrevia {
  id: number;
  fecha_hora: string | null;
  entregado_por: string;
  entregado_por_nombre: string | null;
  metodo: string | null;
  motivo_manual: string | null;
  lat: number | null;
  lng: number | null;
  precision_metros: number | null;
}

export interface TemaActivoResponse {
  tema: Tema | null;
}

export interface MiQRResponse {
  tema: Tema | null;
  recibido?: boolean;
  elegible?: boolean;
  qr?: string;
  ventana?: number;
  ttl_seconds?: number;
  rotacion_seconds?: number;
  cedula?: string;
  nombre?: string;
  entrega?: EntregaPrevia;
  mensaje?: string;
}

export interface MiEstadoResponse {
  tema: Tema | null;
  recibido: boolean;
  entrega: EntregaPrevia | null;
}

export interface MiRolResponse {
  cedula: string;
  tema: Tema | null;
  es_admin: boolean;
  es_entregador: boolean;
  es_elegible: boolean;
}

export interface ConfigResponse {
  tema: Tema;
  incluir_todos: boolean;
  controla_categorias?: boolean;
}

export interface FuncionarioActivo {
  cedula: string;
  nombre: string;
  cargo: string;
}

export interface EntregadorItem {
  cedula: string;
  nombre: string | null;
  cargo: string | null;
  agregado_por: string | null;
  agregado_en: string | null;
  activo_en_dwh: boolean;
}

export type ReceptorItem = EntregadorItem;

export interface EntregaItem {
  id: number;
  cedula_receptor: string;
  receptor_nombre: string | null;
  entregado_por: string;
  entregador_nombre: string | null;
  metodo: string | null;
  motivo_manual: string | null;
  fecha_hora: string | null;
  lat: number | null;
  lng: number | null;
  precision_metros: number | null;
  anulada: boolean;
  anulado_por: string | null;
  anulado_por_nombre: string | null;
  anulado_en: string | null;
  motivo_anulacion: string | null;
  categoria: string | null;
  identificador: string | null;
}

export interface EscanearPayload {
  qr: string;
  lat?: number | null;
  lng?: number | null;
  precision?: number | null;
  categoria?: string | null;
  identificador?: string | null;
}

export interface EntregarManualPayload {
  cedula: string;
  motivo: string;
  lat?: number | null;
  lng?: number | null;
  precision?: number | null;
  tema_id?: number;
  categoria?: string | null;
  identificador?: string | null;
}

export interface EntregaResponse {
  ok: boolean;
  id: number;
  tema: Tema;
  receptor: FuncionarioActivo;
  metodo: string;
  mensaje: string;
  saldo?: number;
}

export interface StockResumen {
  ingresado: number;
  entregado: number;
  consumido?: number;
  en_circulacion: number;
}

export interface StockResumenResponse {
  tema: Tema | null;
  resumen: StockResumen | null;
}

export interface StockMiSaldoResponse {
  tema: Tema | null;
  cedula: string;
  saldo: number;
}

export interface StockSaldoItem {
  cedula: string;
  nombre: string | null;
  saldo: number;
}

export interface StockSaldosResponse {
  tema: Tema | null;
  datos: StockSaldoItem[];
}

export interface StockMovimiento {
  id: number;
  tema_id: number;
  tipo:
    | "ingreso"
    | "transferencia"
    | "entrega"
    | "reverso_entrega"
    | "consumo"
    | "reverso_consumo";
  cedula_origen: string | null;
  cedula_destino: string | null;
  cantidad: number;
  motivo: string | null;
  entrega_id: number | null;
  registrado_por: string;
  fecha_hora: string | null;
  nombre_origen: string | null;
  nombre_destino: string | null;
  nombre_registrado_por: string | null;
  cedula_receptor: string | null;
  nombre_receptor: string | null;
  categoria: string | null;
  identificador: string | null;
  punto_servicio: string | null;
  punto_servicio_nombre: string | null;
}

export interface StockMovimientosResponse {
  tema: Tema | null;
  datos: StockMovimiento[];
}

export interface StockIngresoPayload {
  cedula_destino?: string;
  cantidad: number;
  motivo?: string;
  tema_id?: number;
  categoria?: string | null;
  identificador?: string | null;
  punto_servicio?: string | null;
  punto_servicio_nombre?: string | null;
}

export interface StockIngresoResponse {
  ok: boolean;
  id: number;
  tema: Tema;
  destino: { cedula: string; nombre: string | null };
  cantidad: number;
  categoria?: string | null;
  identificador?: string | null;
  saldo: number;
}

export interface TransferenciaPendiente {
  id: number;
  tema_id: number;
  cedula_origen: string;
  cedula_destino: string;
  cantidad: number;
  categoria: string | null;
  identificador: string | null;
  motivo: string | null;
  estado: string;
  creado_en: string | null;
  resuelto_en: string | null;
  nombre_origen: string | null;
  nombre_destino: string | null;
  punto_servicio?: string | null;
  punto_servicio_nombre?: string | null;
}

export interface TransferenciasPendientesResponse {
  tema: Tema | null;
  recibidas: TransferenciaPendiente[];
  enviadas: TransferenciaPendiente[];
}

export interface TransferenciaSolicitarPayload {
  cedula_destino: string;
  cantidad: number;
  categoria?: string | null;
  identificador?: string | null;
  motivo?: string | null;
  tema_id?: number;
}

export interface StockTransferenciaQRResponse {
  ok: boolean;
  tema: Tema;
  qr: string;
  cantidad: number;
  ts: number;
  ttl_seconds: number;
  saldo: number;
}

export interface StockTransferenciaPreviewResponse {
  ok: boolean;
  tema_id: number;
  cedula_emisor: string;
  nombre_emisor: string | null;
  cantidad: number;
  ts: number;
}

export interface StockTransferenciaAceptarResponse {
  ok: boolean;
  id: number;
  tema: Tema;
  cantidad: number;
  emisor: { cedula: string; nombre: string | null };
  saldo: number;
}

const BASE = "entregas-masivas";

const conTema = (tema_id?: number, extra?: Record<string, unknown>) => ({
  params: tema_id ? { tema_id, ...(extra || {}) } : extra || {},
});

export const obtenerTemaActivo = async (): Promise<TemaActivoResponse> => {
  const { data } = await api.get<TemaActivoResponse>(`${BASE}/tema-activo`);
  return data;
};

export const obtenerTemas = async (): Promise<Tema[]> => {
  const { data } = await api.get<{ datos: Tema[] }>(`${BASE}/temas`);
  return data?.datos || [];
};

export const obtenerMisTemas = async (): Promise<MiTemaItem[]> => {
  const { data } = await api.get<{ datos: MiTemaItem[] }>(`${BASE}/mis-temas`);
  return data?.datos || [];
};

export const crearTema = async (payload: TemaPayload): Promise<Tema> => {
  const { data } = await api.post<{ tema: Tema }>(`${BASE}/temas`, payload);
  return data.tema;
};

export const actualizarTema = async (
  id: number,
  payload: TemaPayload
): Promise<Tema> => {
  const { data } = await api.put<{ tema: Tema }>(`${BASE}/temas/${id}`, payload);
  return data.tema;
};

export const obtenerMiQR = async (tema_id?: number): Promise<MiQRResponse> => {
  const { data } = await api.get<MiQRResponse>(`${BASE}/mi-qr`, conTema(tema_id));
  return data;
};

export const obtenerMiEstado = async (
  tema_id?: number
): Promise<MiEstadoResponse> => {
  const { data } = await api.get<MiEstadoResponse>(
    `${BASE}/mi-estado`,
    conTema(tema_id)
  );
  return data;
};

export const obtenerMiRol = async (
  tema_id?: number
): Promise<MiRolResponse> => {
  const { data } = await api.get<MiRolResponse>(
    `${BASE}/mi-rol`,
    conTema(tema_id)
  );
  return data;
};

export const escanearQR = async (
  payload: EscanearPayload
): Promise<EntregaResponse> => {
  const { data } = await api.post<EntregaResponse>(
    `${BASE}/escanear`,
    payload
  );
  return data;
};

export const entregarManual = async (
  payload: EntregarManualPayload
): Promise<EntregaResponse> => {
  const { tema_id, ...body } = payload;
  const { data } = await api.post<EntregaResponse>(
    `${BASE}/entregar-manual`,
    body,
    conTema(tema_id)
  );
  return data;
};

export const obtenerEntregadores = async (
  tema_id?: number
): Promise<EntregadorItem[]> => {
  const { data } = await api.get<{ datos: EntregadorItem[] }>(
    `${BASE}/entregadores`,
    conTema(tema_id)
  );
  return data?.datos || [];
};

export const actualizarEntregadores = async (
  cedulas: string[],
  tema_id?: number
): Promise<EntregadorItem[]> => {
  const { data } = await api.put<{ datos: EntregadorItem[] }>(
    `${BASE}/entregadores`,
    { cedulas },
    conTema(tema_id)
  );
  return data?.datos || [];
};

export const obtenerReceptores = async (
  tema_id?: number
): Promise<ReceptorItem[]> => {
  const { data } = await api.get<{ datos: ReceptorItem[] }>(
    `${BASE}/receptores`,
    conTema(tema_id)
  );
  return data?.datos || [];
};

export const actualizarReceptores = async (
  cedulas: string[],
  tema_id?: number
): Promise<ReceptorItem[]> => {
  const { data } = await api.put<{ datos: ReceptorItem[] }>(
    `${BASE}/receptores`,
    { cedulas },
    conTema(tema_id)
  );
  return data?.datos || [];
};

export const obtenerConfig = async (
  tema_id?: number
): Promise<ConfigResponse> => {
  const { data } = await api.get<ConfigResponse>(
    `${BASE}/config`,
    conTema(tema_id)
  );
  return data;
};

export const actualizarConfig = async (
  incluir_todos: boolean,
  tema_id?: number
): Promise<ConfigResponse> => {
  const { data } = await api.put<ConfigResponse>(
    `${BASE}/config`,
    { incluir_todos },
    conTema(tema_id)
  );
  return data;
};

export const obtenerEntregas = async (
  limite: number = 500,
  tema_id?: number
): Promise<EntregaItem[]> => {
  const { data } = await api.get<{ datos: EntregaItem[] }>(
    `${BASE}/entregas`,
    { params: { limite, ...(tema_id ? { tema_id } : {}) } }
  );
  return data?.datos || [];
};

export const anularEntrega = async (
  id: number,
  motivo: string
): Promise<void> => {
  await api.post(`${BASE}/entregas/${id}/anular`, { motivo });
};

export const buscarFuncionarios = async (
  q?: string
): Promise<FuncionarioActivo[]> => {
  const { data } = await api.get<{ datos: FuncionarioActivo[] }>(
    `${BASE}/funcionarios`,
    { params: q ? { q } : {} }
  );
  return data?.datos || [];
};

// ============================================================================
// Stock
// ============================================================================

export const obtenerStockResumen = async (
  tema_id?: number,
  categoria?: string
): Promise<StockResumenResponse> => {
  const { data } = await api.get<StockResumenResponse>(
    `${BASE}/stock/resumen`,
    conTema(tema_id, categoria ? { categoria } : undefined)
  );
  return data;
};

export const obtenerStockBuckets = async (
  tema_id?: number,
  cedula?: string
): Promise<StockBucket[]> => {
  const { data } = await api.get<{ datos: StockBucket[] }>(
    `${BASE}/stock/buckets`,
    conTema(tema_id, cedula ? { cedula } : undefined)
  );
  return data?.datos || [];
};

export const obtenerMiSaldo = async (
  tema_id?: number
): Promise<StockMiSaldoResponse> => {
  const { data } = await api.get<StockMiSaldoResponse>(
    `${BASE}/stock/mi-saldo`,
    conTema(tema_id)
  );
  return data;
};

export const obtenerStockSaldos = async (
  tema_id?: number
): Promise<StockSaldosResponse> => {
  const { data } = await api.get<StockSaldosResponse>(
    `${BASE}/stock/saldos`,
    conTema(tema_id)
  );
  return data;
};

export const cargarIngresoStock = async (
  payload: StockIngresoPayload
): Promise<StockIngresoResponse> => {
  const { tema_id, ...body } = payload;
  const { data } = await api.post<StockIngresoResponse>(
    `${BASE}/stock/ingreso`,
    body,
    conTema(tema_id)
  );
  return data;
};

export const generarQRTransferencia = async (
  cantidad: number,
  tema_id?: number
): Promise<StockTransferenciaQRResponse> => {
  const { data } = await api.post<StockTransferenciaQRResponse>(
    `${BASE}/stock/transferencia/qr`,
    { cantidad },
    conTema(tema_id)
  );
  return data;
};

export const previewTransferencia = async (
  qr: string
): Promise<StockTransferenciaPreviewResponse> => {
  const { data } = await api.post<StockTransferenciaPreviewResponse>(
    `${BASE}/stock/transferencia/preview`,
    { qr }
  );
  return data;
};

export const aceptarTransferencia = async (
  qr: string,
  motivo?: string
): Promise<StockTransferenciaAceptarResponse> => {
  const { data } = await api.post<StockTransferenciaAceptarResponse>(
    `${BASE}/stock/transferencia/aceptar`,
    { qr, motivo }
  );
  return data;
};

export const obtenerMovimientosStock = async (
  opts: {
    tema_id?: number;
    cedula?: string;
    tipo?: string;
    limite?: number;
    categoria?: string;
    identificador?: string;
  } = {}
): Promise<StockMovimientosResponse> => {
  const params: Record<string, unknown> = {};
  if (opts.tema_id) params.tema_id = opts.tema_id;
  if (opts.cedula) params.cedula = opts.cedula;
  if (opts.tipo) params.tipo = opts.tipo;
  if (opts.limite) params.limite = opts.limite;
  if (opts.categoria) params.categoria = opts.categoria;
  if (opts.identificador) params.identificador = opts.identificador;
  const { data } = await api.get<StockMovimientosResponse>(
    `${BASE}/stock/movimientos`,
    { params }
  );
  return data;
};

// ============================================================================
// Transferencias dirigidas (sin QR)
// ============================================================================

export const solicitarTransferencia = async (
  payload: TransferenciaSolicitarPayload
): Promise<{ ok: boolean; id: number; destino: { cedula: string; nombre: string | null }; cantidad: number }> => {
  const { tema_id, ...body } = payload;
  const { data } = await api.post(
    `${BASE}/stock/transferencia/solicitar`,
    body,
    conTema(tema_id)
  );
  return data;
};

export interface RecepcionPendiente extends TransferenciaPendiente {
  tema_nombre: string;
}

export const obtenerRecepcionesPendientes = async (): Promise<
  RecepcionPendiente[]
> => {
  const { data } = await api.get<{ datos: RecepcionPendiente[] }>(
    `${BASE}/recepciones-pendientes`
  );
  return data?.datos || [];
};

export const obtenerTransferenciasPendientesTema = async (
  tema_id?: number
): Promise<TransferenciaPendiente[]> => {
  const { data } = await api.get<{ datos: TransferenciaPendiente[] }>(
    `${BASE}/stock/transferencias/pendientes-tema`,
    conTema(tema_id)
  );
  return data?.datos || [];
};

export const obtenerTransferenciasPendientes = async (
  tema_id?: number
): Promise<TransferenciasPendientesResponse> => {
  const { data } = await api.get<TransferenciasPendientesResponse>(
    `${BASE}/stock/transferencias/pendientes`,
    conTema(tema_id)
  );
  return data;
};

export const aceptarTransferenciaDirecta = async (
  id: number
): Promise<StockTransferenciaAceptarResponse> => {
  const { data } = await api.post<StockTransferenciaAceptarResponse>(
    `${BASE}/stock/transferencia/${id}/aceptar`,
    {}
  );
  return data;
};

export const rechazarTransferencia = async (id: number): Promise<void> => {
  await api.post(`${BASE}/stock/transferencia/${id}/rechazar`, {});
};

export const cancelarTransferencia = async (id: number): Promise<void> => {
  await api.post(`${BASE}/stock/transferencia/${id}/cancelar`, {});
};

// ============================================================================
// Consumo / instalacion
// ============================================================================

export interface ConsumoPayload {
  tema_id?: number;
  categoria?: string | null;
  identificador?: string | null;
  cantidad?: number;
  // El PS se hereda del ingreso del item; se puede omitir y lo resuelve el backend.
  punto_servicio?: string | null;
  punto_servicio_nombre?: string | null;
  lat?: number | null;
  lng?: number | null;
  precision?: number | null;
  motivo?: string | null;
}

export const registrarConsumo = async (
  payload: ConsumoPayload
): Promise<{ ok: boolean; id: number; saldo: number }> => {
  const { tema_id, ...body } = payload;
  const { data } = await api.post(`${BASE}/stock/consumo`, body, conTema(tema_id));
  return data;
};

export const revertirConsumo = async (
  id: number,
  motivo?: string
): Promise<void> => {
  await api.post(`${BASE}/stock/consumo/${id}/revertir`, { motivo });
};
